import {
  SitePhoto,
  UpdateSitePhotoRequest,
  UploadPhotoArgs,
} from '../models/SitePhoto';
import { API_ENDPOINTS } from '../config/api';
import { apiClient } from './api/apiClient';
import { withMediaToken } from '../utils/media';
import {
  ApiItemResponse,
  ApiListResponse,
  ApiOkResponse,
  ensureOk,
  unwrapItem,
  unwrapList,
} from './api/response';

export class SitePhotoService {
  static async list(projectId: number, category?: string): Promise<SitePhoto[]> {
    const response = await apiClient.get<ApiListResponse<SitePhoto>>(
      API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.LIST(projectId, category),
    );
    return unwrapList(response, '사진 목록 조회 실패');
  }

  static async upload(projectId: number, args: UploadPhotoArgs): Promise<SitePhoto> {
    const form = new FormData();
    form.append('file', args.file, args.file.name);
    form.append('category', args.category);
    if (args.note) form.append('note', args.note);
    if (args.takenAt) form.append('taken_at', args.takenAt);
    if (args.uploadedBy) form.append('uploaded_by', args.uploadedBy);
    if (args.relatedIncidentId != null) {
      form.append('related_incident_id', String(args.relatedIncidentId));
    }
    if (args.relatedVisitLogId != null) {
      form.append('related_visit_log_id', String(args.relatedVisitLogId));
    }

    const response = await apiClient.post<ApiItemResponse<SitePhoto>>(
      API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.UPLOAD(projectId),
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return unwrapItem(response, '사진 업로드 실패');
  }

  static async update(photoId: number, data: UpdateSitePhotoRequest): Promise<SitePhoto> {
    const response = await apiClient.put<ApiItemResponse<SitePhoto>>(
      API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.UPDATE(photoId),
      data,
    );
    return unwrapItem(response, '사진 수정 실패');
  }

  static async delete(photoId: number): Promise<void> {
    const response = await apiClient.delete<ApiOkResponse>(
      API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.DELETE(photoId),
    );
    ensureOk(response, '사진 삭제 실패');
  }

  /**
   * 사진 파일 URL. `<img src=...>` / anchor download 처럼 axios interceptor 를
   * 타지 않는 요청에서 직접 사용한다. 브라우저가 Authorization 헤더를 못
   * 붙이므로, 백엔드 `AuthMiddlewareAllowQueryToken` 가 받는 `?token=` 쿼리에
   * 현재 access token 을 실어 보낸다. 토큰이 없으면(미로그인) 쿼리 없이 반환
   * 하고 백엔드가 401 로 응답하도록 둔다.
   */
  static fileUrl(photoId: number): string {
    return withMediaToken(API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.FILE(photoId));
  }

  /**
   * 토큰을 붙이지 않은 사진 파일 상대 경로. 에디터 본문(마크다운)에 인라인
   * 이미지로 저장할 때 사용한다 — 만료되는 `?token=` 을 본문에 굳히지 않고,
   * 렌더 시점에 MediaImage 확장이 withMediaToken 으로 토큰을 붙인다.
   */
  static filePath(photoId: number): string {
    return API_ENDPOINTS.SITE_MANAGEMENT.PHOTOS.FILE(photoId);
  }
}
